(function () {
  var enterModule = typeof reactHotLoaderGlobal !== 'undefined' ? reactHotLoaderGlobal.enterModule : undefined;
  enterModule && enterModule(module);
})();

var __signature__ = typeof reactHotLoaderGlobal !== 'undefined' ? reactHotLoaderGlobal.default.signature : function (a) {
  return a;
};

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import ChildWarning from "../ChildWarning";
import Loading from "../../Loading";
import Badge from "../../Badge";
import Context from "../context";
import styles from "./HsTree.module.css";
import { isCodePartOf } from "../utils";
import { ChevronDown } from "../../../icons/ChevronDown";
import { ChevronRight } from "../../../icons/ChevronRight";
const MAX_INDENT = 48; // max indention for vertical line in px;

const HsTree = props => {
  const {
    code,
    description,
    title,
    collapsable,
    bold,
    isLoading,
    searchCode,
    onOpen,
    warningMessage,
    children,
    isSubTree,
    colors,
    isRtl
  } = props;
  const isCodePartOfSearchCode = useMemo(() => isCodePartOf(code, searchCode), [code, searchCode]);
  const [isOpen, setIsOpen] = useState(!collapsable || isCodePartOfSearchCode && children || undefined);
  const [badgeWidth, setBadgeWidth] = useState(0);
  const badgeRef = useRef();
  useEffect(() => {
    if (badgeRef.current) {
      setBadgeWidth(badgeRef.current.getBoundingClientRect().width);
    }
  }, []);
  useEffect(() => {
    if (collapsable && isOpen === undefined && isCodePartOfSearchCode && children) {
      // open on Initial load for collapsable mode
      setIsOpen(true);
    }
  }, [collapsable, children, isCodePartOfSearchCode, isOpen]);
  const onItemClick = useCallback(() => {
    if (collapsable) {
      setIsOpen(val => {
        if (!val) {
          onOpen(code);
        }

        return !val;
      });
    } else {
      onOpen(code);
    }
  }, [code, collapsable, onOpen]);
  const verticalLinePosition = useMemo(() => {
    const val = badgeWidth / 2 + (collapsable ? 14 : 0) + 15;
    return isSubTree && val > MAX_INDENT ? MAX_INDENT : val;
  }, [badgeWidth, collapsable, isSubTree]); // eslint-disable-next-line no-nested-ternary

  const badgeBgColor = title ? "inherit" : isCodePartOfSearchCode || !searchCode ? colors.success : colors.secondary;
  const badgeTextColor = title ? colors.info : undefined;
  return /*#__PURE__*/React.createElement(Context.Provider, {
    value: {
      verticalLinePosition,
      colors
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: cx(styles.treeRoot, {
      [styles.subTreeRoot]: isSubTree
    })
  }, /*#__PURE__*/React.createElement("div", {
    className: cx(styles.rootBadge, {
      [styles.pointer]: collapsable
    }),
    onClick: onItemClick
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Badge, {
    ref: badgeRef,
    color: badgeBgColor,
    textColor: badgeTextColor
  }, title || code), collapsable && /*#__PURE__*/React.createElement("div", {
    className: cx(styles.arrowBox, {
      [styles.rtl]: isRtl
    })
  }, isOpen ? /*#__PURE__*/React.createElement(ChevronDown, {
    className: cx(styles.arrow, {
      [styles.rtl]: isRtl
    }),
    color: "#66758f"
  }) : /*#__PURE__*/React.createElement(ChevronRight, {
    className: cx(styles.arrow, {
      [styles.rtl]: isRtl && !isOpen
    }),
    color: "#66758f"
  }), isLoading && /*#__PURE__*/React.createElement(Loading, {
    thickness: 5,
    className: styles.loading,
    color: colors.success
  })))), /*#__PURE__*/React.createElement("div", {
    className: cx(styles.treeItemDetails, {
      [styles.pointer]: collapsable,
      [styles.rtl]: isRtl
    }),
    style: {
      marginLeft: isSubTree && !isRtl ? verticalLinePosition + 15 : 0,
      marginRight: isSubTree && isRtl ? verticalLinePosition + 15 : 0
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: cx(styles.label, {
      [styles.verticalLine]: isOpen && children && children.length,
      [styles.bold]: bold,
      [styles.rtl]: isRtl
    }),
    onClick: onItemClick,
    style: {
      "--vertical-line-position-left": "".concat(-verticalLinePosition, "px")
    }
  }, description), isOpen && /*#__PURE__*/React.createElement("ul", {
    className: styles.list
  }, warningMessage && /*#__PURE__*/React.createElement(ChildWarning, null, warningMessage), children))));
};

__signature__(HsTree, "useMemo{isCodePartOfSearchCode}\nuseState{[isOpen, setIsOpen](!collapsable || (isCodePartOfSearchCode && children) || undefined)}\nuseState{[badgeWidth, setBadgeWidth](0)}\nuseRef{badgeRef}\nuseEffect{}\nuseEffect{}\nuseCallback{onItemClick}\nuseMemo{verticalLinePosition}");

HsTree.propTypes = {
  children: PropTypes.node,
  warningMessage: PropTypes.string,
  searchCode: PropTypes.string,
  code: PropTypes.string,
  description: PropTypes.string,
  title: PropTypes.string,
  collapsable: PropTypes.bool,
  isLoading: PropTypes.bool,
  bold: PropTypes.bool,
  onOpen: PropTypes.func,
  isSubTree: PropTypes.bool,
  colors: PropTypes.shape({
    success: PropTypes.string,
    info: PropTypes.string,
    secondary: PropTypes.string
  }),
  isRtl: PropTypes.bool
};
HsTree.defaultProps = {
  children: null,
  warningMessage: null,
  code: null,
  title: null,
  searchCode: null,
  description: null,
  collapsable: false,
  isLoading: false,
  bold: false,
  isSubTree: false,
  colors: {
    info: "#404b6c",
    // used for heading code
    success: "#00bea6",
    // used for selected code
    secondary: "#8f93af" // used for code

  },
  onOpen: () => {},
  isRtl: false
};

const _default = /*#__PURE__*/React.memo(HsTree);

export default _default;
;

(function () {
  var reactHotLoader = typeof reactHotLoaderGlobal !== 'undefined' ? reactHotLoaderGlobal.default : undefined;

  if (!reactHotLoader) {
    return;
  }

  reactHotLoader.register(MAX_INDENT, "MAX_INDENT", "/root/local/TeamCity/buildAgent/work/bac1af6cbd11267e/src/components/HsCodesTree/HsTree/HsTree.jsx");
  reactHotLoader.register(HsTree, "HsTree", "/root/local/TeamCity/buildAgent/work/bac1af6cbd11267e/src/components/HsCodesTree/HsTree/HsTree.jsx");
  reactHotLoader.register(_default, "default", "/root/local/TeamCity/buildAgent/work/bac1af6cbd11267e/src/components/HsCodesTree/HsTree/HsTree.jsx");
})();

;

(function () {
  var leaveModule = typeof reactHotLoaderGlobal !== 'undefined' ? reactHotLoaderGlobal.leaveModule : undefined;
  leaveModule && leaveModule(module);
})();